import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, CheckCircle, AlertTriangle, Download, Printer} from 'lucide-react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

interface Question {
  id: number
  text: string
}

const socialAnxietyQuestions: Question[] = [
  { id: 1, text: 'I feel anxious or nervous in social situations' },
  { id: 2, text: 'I avoid social events or gatherings' },
  { id: 3, text: 'I worry about embarrassing myself in front of others' },
  { id: 4, text: 'I feel self-conscious when people watch me' },
  { id: 5, text: 'I fear being judged or criticized by others' },
  { id: 6, text: 'Speaking in public or in groups makes me very anxious' },
  { id: 7, text: 'I have difficulty making eye contact during conversations' },
  { id: 8, text: 'I worry for days before a social event' },
  { id: 9, text: 'Physical symptoms (sweating, trembling, blushing) occur in social settings' },
  { id: 10, text: 'My fear of social situations interferes with my work, school, or relationships' }
]

const scoreOptions = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Always' }
]

export default function SocialAnxietyAssessment() {
  const [answers, setAnswers] = useState<{ [key: number]: number }>({})
  const [showResults, setShowResults] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers({ ...answers, [questionId]: value })
  }

  const calculateScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0)
  }

  const handleSubmit = () => {
    if (Object.keys(answers).length === 10) {
      setShowResults(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPDF = async () => {
    if (!resultsRef.current) return
    
    setIsGeneratingPDF(true)
    try {
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      })
      
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`Social-Anxiety-Results-${new Date().toISOString().split('T')[0]}.pdf`)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again or use the Print option.')
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const getScoreInterpretation = (score: number) => {
    if (score <= 10) {
      return {
        level: 'Low Social Anxiety',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: 'Your responses suggest minimal social anxiety. You may experience occasional nervousness in social situations, which is normal.',
        recommendation: 'Continue engaging in social activities. Practice self-compassion and maintain healthy social connections.'
      }
    } else if (score <= 20) {
      return {
        level: 'Mild Social Anxiety',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        description: 'Your responses indicate mild social anxiety that may cause some discomfort but is generally manageable.',
        recommendation: 'Consider gradual exposure to social situations. Self-help resources and social skills practice can be beneficial. Monitor if symptoms worsen.'
      }
    } else if (score <= 30) {
      return {
        level: 'Moderate Social Anxiety',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        description: 'Your responses suggest moderate social anxiety that may be limiting your social or professional life.',
        recommendation: 'We recommend consulting with a mental health professional. Cognitive Behavioral Therapy (CBT) and exposure therapy are highly effective for social anxiety disorder.'
      }
    } else {
      return {
        level: 'Severe Social Anxiety',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        description: 'Your responses indicate severe social anxiety (social phobia) that significantly interferes with daily functioning.',
        recommendation: 'Please consult with a mental health professional specializing in anxiety disorders. Treatment typically includes CBT, exposure therapy, and sometimes medication (SSRIs). Social anxiety disorder is highly treatable.'
      }
    }
  }

  const score = calculateScore()
  const interpretation = getScoreInterpretation(score)
  const isComplete = Object.keys(answers).length === 10

  return (
    <div>
      {/* Results Section */}
      {showResults && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6 print:hidden">
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </>
                )}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-md"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </button>
            </div>
            
            <div ref={resultsRef} className={`${interpretation.bgColor} border-2 ${interpretation.borderColor} rounded-xl p-8`}>
              <div className="text-center mb-6">
                <div className="text-6xl font-bold mb-2 text-gray-900">{score}</div>
                <div className={`text-2xl font-bold ${interpretation.color}`}>{interpretation.level}</div>
              </div>
              <div className="bg-white rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your Results</h3>
                <p className="text-gray-700 mb-4">{interpretation.description}</p>
                <p className="text-gray-700"><strong>Recommendation:</strong> {interpretation.recommendation}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
                <Link
                  to="/therapy"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all"
                >
                  Find a Therapist
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <button
                  onClick={() => {
                    setAnswers({})
                    setShowResults(false)
                  }}
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all"
                >
                  Retake Assessment
                </button>
              </div>
              
              <div className="hidden print:block mt-8 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-600 text-center">
                  Generated by Anxiety USA - anxietyusa.com | Date: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* About Social Anxiety */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">About Social Anxiety Assessment</h2>
          <div className="prose prose-lg max-w-none text-gray-700 mb-8">
            <p className="mb-4">
              <strong>Social Anxiety Disorder</strong> (also called social phobia) affects approximately 7% of Americans. It involves intense fear of social situations where you might be judged, embarrassed, or scrutinized by others.
            </p>
            <p className="mb-4">
              This screening tool assesses the severity of social anxiety symptoms across different domains: fear, avoidance, physical symptoms, and functional impairment. Unlike general anxiety, social anxiety is specifically triggered by social or performance situations.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-[#4A90E2] p-6 rounded-r-lg mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Understanding Your Score</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="font-bold text-green-600 w-20 flex-shrink-0">0-10:</div>
                <div className="text-gray-700">Low social anxiety (within normal range)</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-yellow-600 w-20 flex-shrink-0">11-20:</div>
                <div className="text-gray-700">Mild social anxiety</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-orange-600 w-20 flex-shrink-0">21-30:</div>
                <div className="text-gray-700">Moderate social anxiety</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-red-600 w-20 flex-shrink-0">31-40:</div>
                <div className="text-gray-700">Severe social anxiety disorder</div>
              </li>
            </ul>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
            <h4 className="font-bold text-gray-900 mb-3">Common Social Anxiety Triggers</h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Public speaking or presentations
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Meeting new people
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Eating or drinking in public
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Being the center of attention
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Making phone calls
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Job interviews or performance reviews
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Social Anxiety Questionnaire */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Social Anxiety Screening</h2>
            <p className="text-center text-gray-600 mb-8">
              How often do you experience the following in social situations?
            </p>

            <div className="space-y-8">
              {socialAnxietyQuestions.map((question) => (
                <div key={question.id} className="pb-6 border-b border-gray-200 last:border-b-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {question.id}. {question.text}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {scoreOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleAnswer(question.id, option.value)}
                        className={`p-4 rounded-lg border-2 transition-all text-center ${
                          answers[question.id] === option.value
                            ? 'border-[#4A90E2] bg-blue-50 text-[#4A90E2] font-semibold'
                            : 'border-gray-200 hover:border-[#48C9B0] text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={handleSubmit}
                disabled={!isComplete}
                className={`inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold transition-all ${
                  isComplete
                    ? 'bg-[#4A90E2] text-white hover:bg-[#3A7BC8] shadow-lg cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isComplete ? (
                  <>
                    Calculate My Score
                    <CheckCircle className="ml-2 w-5 h-5" />
                  </>
                ) : (
                  <>Answer all questions to see results</>
                )}
              </button>
              <p className="text-sm text-gray-500 mt-4">
                {Object.keys(answers).length} of 10 questions answered
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}